import {
  Smartphone,
  Chrome,
  Code,
  Monitor,
  Sparkles,
  Database,
  type LucideIcon,
} from "lucide-react";

export interface FeatureBenefit {
  title: string;
  description: string;
}

export interface FeatureFAQ {
  q: string;
  a: string;
}

export interface FeaturePage {
  slug: string;
  icon: LucideIcon;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  intro: string;
  primaryCta: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  benefits: FeatureBenefit[];
  howItWorks: { title: string; body: string }[];
  useCases: string[];
  faqs: FeatureFAQ[];
}

export const featurePages: FeaturePage[] = [
  {
    slug: "android-autofill",
    icon: Smartphone,
    metaTitle:
      "Android Autofill for QA & Test Data | TestAssets",
    metaDescription:
      "Use TestAssets as your Android autofill service to paste test credentials, IDs, and sample inputs into any app — perfect for QA and mobile testers.",
    eyebrow: "Android",
    h1: "Android Autofill for QA Engineers and Asset Managers",
    intro:
      "TestAssets registers as a native Android Autofill Service. Pick a test asset from a system-level picker and paste it into any app's input field — no copy, no paste, no typo.",
    primaryCta: { label: "Set up Android autofill", to: "/setup-quickfill" },
    secondaryCta: { label: "How it works", to: "/how-it-works" },
    benefits: [
      {
        title: "Native autofill, not a clipboard hack",
        description:
          "Built on Android's official AutofillService API. Works in any app that exposes input fields — no per-app integration required.",
      },
      {
        title: "Your test catalog at the field",
        description:
          "All your asset lists — staging logins, test cards, sample addresses — surface inline at the input that needs them.",
      },
      {
        title: "Offline-friendly cache",
        description:
          "The autofill service reads from a local cache so it works even when the device is on flaky test-lab Wi-Fi.",
      },
      {
        title: "Privacy-first",
        description:
          "TestAssets never auto-saves the values you fill — it only offers what you explicitly added to your library.",
      },
    ],
    howItWorks: [
      {
        title: "Install the Android app",
        body: "Get TestAssets from Google Play or sideload the latest APK.",
      },
      {
        title: "Enable the autofill service",
        body: "In Android Settings → Passwords & accounts → Autofill service, pick TestAssets.",
      },
      {
        title: "Tap any input, pick an asset",
        body: "When you focus a field, the TestAssets picker appears above the keyboard. Tap a value to fill.",
      },
    ],
    useCases: [
      "Mobile QA testing staging logins on real devices",
      "Cross-app testing with shared test accounts",
      "Field engineers pasting fixed customer references",
      "Demo recordings where you don't want to type credentials on screen",
    ],
    faqs: [
      {
        q: "Does this replace my password manager?",
        a: "No. TestAssets is built for non-secret test data — staging credentials, IDs, sample inputs. You can keep your password manager active and switch services per app.",
      },
      {
        q: "Which Android versions are supported?",
        a: "Android 8.0 (API 26) and newer, which is when AutofillService became available.",
      },
      {
        q: "Does TestAssets see what I type?",
        a: "No. The autofill service only reads asset values from your local cache and offers them when a field is focused. It never reads what you type.",
      },
    ],
  },
  {
    slug: "chrome-extension",
    icon: Chrome,
    metaTitle:
      "Chrome Extension for Test Data Autofill | TestAssets",
    metaDescription:
      "The TestAssets Chrome extension pastes test credentials, IDs, and fixtures into any web form — built for QA engineers, not password managers.",
    eyebrow: "Browser",
    h1: "Chrome Extension for Test Data Autofill",
    intro:
      "Stop hunting through wikis for the staging admin password. The TestAssets Chrome extension surfaces your test asset library on every web form, one keyboard shortcut away.",
    primaryCta: { label: "Get the Chrome extension", to: "/applications" },
    secondaryCta: { label: "See pricing", to: "/pricing" },
    benefits: [
      {
        title: "Built for testers, not consumers",
        description:
          "Unlike password managers, TestAssets is happy with hundreds of throwaway accounts, sample emails, and sandbox API keys.",
      },
      {
        title: "Keyboard-first paste",
        description:
          "Trigger the picker from any input field with a shortcut. No mouse, no context-menu spelunking.",
      },
      {
        title: "Synced across your browsers",
        description:
          "Your asset library lives in the cloud, so the extension stays in sync with the desktop app, mobile, and web.",
      },
      {
        title: "Works on every site",
        description:
          "Any input, any domain — including SaaS admin panels and internal tools that other autofill tools refuse to touch.",
      },
    ],
    howItWorks: [
      {
        title: "Install from the Chrome Web Store",
        body: "Add the TestAssets extension and sign in with your account.",
      },
      {
        title: "Focus an input on any page",
        body: "Use the shortcut to open the asset picker.",
      },
      {
        title: "Pick an asset to paste it in place",
        body: "Search by name, list, or tag. The selected value is pasted exactly where the cursor is.",
      },
    ],
    useCases: [
      "QA testing staging dashboards with shared logins",
      "Filling demo forms during sales calls",
      "Pasting recurring sandbox API keys into config UIs",
      "Onboarding new engineers without sharing a wiki page of secrets",
    ],
    faqs: [
      {
        q: "Does this work in Firefox or Edge?",
        a: "Yes. There's a dedicated Firefox build, and the Chrome extension installs cleanly in Edge and other Chromium browsers.",
      },
      {
        q: "Will it interfere with my password manager?",
        a: "No. TestAssets is shortcut-triggered, so it only appears when you ask for it — your password manager keeps owning login forms.",
      },
      {
        q: "Where are my assets stored?",
        a: "In your TestAssets account. The extension syncs through the same backend as desktop and mobile.",
      },
    ],
  },
  {
    slug: "test-data-api",
    icon: Code,
    metaTitle:
      "Test Data REST API for Test Assets | TestAssets",
    metaDescription:
      "Pull your test assets, lists, and fixtures into CI pipelines, Playwright suites, and load tests with the TestAssets REST API.",
    eyebrow: "Developer",
    h1: "REST API for Your Test Assets and Test Data",
    intro:
      "Every asset in your TestAssets library is also available over a simple REST API — so your CI jobs, Playwright suites, k6 tests, and internal tools can read the same data your QA team uses by hand.",
    primaryCta: { label: "Read the API docs", to: "/api-docs" },
    secondaryCta: { label: "Try the playground", to: "/api-docs" },
    benefits: [
      {
        title: "One source of truth",
        description:
          "Stop duplicating fixtures between repos. Your tests pull from the same library your QA engineers manage.",
      },
      {
        title: "Simple API key auth",
        description:
          "Generate scoped keys per environment. Rotate without redeploying.",
      },
      {
        title: "Fast and rate-limited",
        description:
          "Built on edge functions with sensible per-endpoint rate limits, so your CI pipelines stay snappy and protected.",
      },
      {
        title: "Works with anything that speaks HTTP",
        description:
          "Playwright, Cypress, k6, Postman, GitHub Actions, custom Bash — if it can curl, it can read your test data.",
      },
    ],
    howItWorks: [
      {
        title: "Generate an API key",
        body: "From Settings → API, create a key for the environment you want to read from.",
      },
      {
        title: "Call /assets or /asset-lists",
        body: "Pass the apikey header. Search and filter by list, tag, or text.",
      },
      {
        title: "Use the values in your pipeline",
        body: "Inject them into env vars, fixtures, or test setup hooks.",
      },
    ],
    useCases: [
      "Playwright suites pulling staging logins at runtime",
      "k6 load tests pulling 10,000 sample inputs",
      "GitHub Actions setting up env vars from a shared list",
      "Internal admin tools reading sandbox identifiers",
    ],
    faqs: [
      {
        q: "Is there a rate limit?",
        a: "Yes — fixed-window per-endpoint limits. The numbers are documented in the API reference, and the headers report your remaining quota on every response.",
      },
      {
        q: "Can I write through the API?",
        a: "Yes. /assets and /asset-lists support full CRUD for keys with the right scope.",
      },
      {
        q: "Is there an MCP integration?",
        a: "Yes — see the MCP Server feature page for AI-agent access to the same data.",
      },
    ],
  },
  {
    slug: "desktop-quick-paste",
    icon: Monitor,
    metaTitle:
      "Desktop Quick Paste for QA — Windows & macOS | TestAssets",
    metaDescription:
      "Paste test credentials, IDs, and fixtures into any Windows or Mac app from a global hotkey. The TestAssets desktop app pastes at the cursor — no clipboard pollution.",
    eyebrow: "Desktop",
    h1: "Desktop Quick Paste for Windows and macOS",
    intro:
      "The TestAssets desktop app puts your test asset library behind a global hotkey. Trigger it from any application — IDE, terminal, browser, native app — pick a value, and it's pasted at the cursor. No clipboard side effects.",
    primaryCta: { label: "Download for Mac & Windows", to: "/applications" },
    secondaryCta: { label: "How it works", to: "/how-it-works" },
    benefits: [
      {
        title: "Pastes at the cursor, not the clipboard",
        description:
          "Simulates the keystrokes directly into the focused field. Your clipboard stays untouched.",
      },
      {
        title: "Works in any native app",
        description:
          "VS Code, Postman, terminal, Slack, IntelliJ, browsers, internal Electron tools — anything that accepts keyboard input.",
      },
      {
        title: "Background-friendly",
        description:
          "Runs in the menu bar / system tray. The picker opens in milliseconds when you press the hotkey.",
      },
      {
        title: "Same library as web and mobile",
        description:
          "Edits sync across devices, so your laptop has what your phone has.",
      },
    ],
    howItWorks: [
      {
        title: "Install the desktop app",
        body: "Download the latest release for macOS or Windows from the Applications page.",
      },
      {
        title: "Sign in and pick a hotkey",
        body: "Default is a global shortcut you can rebind from desktop settings.",
      },
      {
        title: "Press the hotkey, pick, paste",
        body: "The picker shows your asset library. Selecting a value pastes it at the cursor of the previously-focused app.",
      },
    ],
    useCases: [
      "QA pasting test credentials into a Postman request",
      "DevOps engineers pasting recurring k8s namespace strings",
      "Support engineers pasting templated reply snippets",
      "Demo speakers pasting long IDs without typos",
    ],
    faqs: [
      {
        q: "Does it work offline?",
        a: "Yes. The desktop app caches your asset library locally, so the picker opens and pastes without a network round-trip.",
      },
      {
        q: "Does it record what I paste into?",
        a: "No. The app only knows that the previous-focused window was a target — it does not read the contents of any other application.",
      },
      {
        q: "Is the desktop app open source?",
        a: "It uses many open source components — see the Open Source Licenses page for the full list.",
      },
    ],
  },
  {
    slug: "mcp-server",
    icon: Sparkles,
    metaTitle:
      "MCP Server for Test Data — AI Agent Access | TestAssets",
    metaDescription:
      "Connect Claude, Cursor, and other AI agents to your TestAssets library through the Model Context Protocol (MCP) — agents can now read and write your test data.",
    eyebrow: "AI",
    h1: "MCP Server for Your Test Data",
    intro:
      "TestAssets ships an MCP (Model Context Protocol) server so AI agents like Claude Desktop and Cursor can read, search, and create test assets in your account — turning your library into a tool any modern coding agent can use.",
    primaryCta: { label: "Read the MCP docs", to: "/mcp-docs" },
    secondaryCta: { label: "API reference", to: "/api-docs" },
    benefits: [
      {
        title: "Standards-compliant MCP",
        description:
          "Built on the official Model Context Protocol spec, so any MCP-aware client can connect.",
      },
      {
        title: "Agents that know your test data",
        description:
          "Ask your agent to write a Playwright spec using your staging admin login — it pulls the live value, not a placeholder.",
      },
      {
        title: "Scoped, revocable access",
        description:
          "Agent access uses the same API key system as the REST API. Rotate at any time.",
      },
      {
        title: "Read and write tools",
        description:
          "Agents can list, search, create, and update assets and asset lists — useful for bulk fixture generation.",
      },
    ],
    howItWorks: [
      {
        title: "Generate an API key",
        body: "From Settings → API, mint a key for your AI agent.",
      },
      {
        title: "Add the MCP server to your client",
        body: "Paste the configuration snippet from the MCP docs into Claude Desktop, Cursor, or any MCP client.",
      },
      {
        title: "Start prompting",
        body: "Your agent now has tools for listing, searching, and writing test assets.",
      },
    ],
    useCases: [
      "Claude or Cursor writing test specs that reference live test accounts",
      "Agents bulk-generating fixtures into a named list",
      "AI assistants answering 'what test card do I use for declined Visa?' from your library",
      "Internal LLM workflows reading staging config",
    ],
    faqs: [
      {
        q: "Which MCP clients are supported?",
        a: "Any client that implements the Model Context Protocol — Claude Desktop, Cursor, and others. Configuration snippets for the most common ones are in the MCP docs.",
      },
      {
        q: "Can the agent see all my data?",
        a: "Only what the API key it's configured with can see. Use scoped keys to limit access.",
      },
      {
        q: "Is there a separate price for MCP?",
        a: "No. MCP usage counts against the same API quota as REST. See the pricing page for details.",
      },
    ],
  },
  {
    slug: "test-data-generator",
    icon: Database,
    metaTitle:
      "Test Data Generator for QA Teams — Free & Hosted | TestAssets",
    metaDescription:
      "TestAssets is a hosted test data generator for QA and dev teams: curate reusable test data, generate fake data on demand, and paste it into any form, app, or API.",
    eyebrow: "Test data",
    h1: "Test Data Generator for QA and Engineering Teams",
    intro:
      "Most test data generators spit out random rows you throw away after one run. TestAssets is different: a hosted test data generator and curated catalog in one — generate fake data, save the assets you actually reuse (staging logins, sample IDs, fixtures), and paste them into any web form, desktop app, or API call.",
    primaryCta: { label: "Start generating test data", to: "/auth" },
    secondaryCta: { label: "See how it works", to: "/how-it-works" },
    benefits: [
      {
        title: "Generate and curate in one place",
        description:
          "Bulk-generate fake names, emails, addresses, UUIDs, and phone numbers — then keep the values you actually want to reuse as named assets your whole team can paste later.",
      },
      {
        title: "Beyond a fake data generator",
        description:
          "Random faker-style output is great until you need the same staging admin login twice. TestAssets remembers, organizes, and shares the dummy data your team actually relies on.",
      },
      {
        title: "Paste anywhere — web, desktop, mobile, API",
        description:
          "Browser extension, macOS/Windows desktop app, Android autofill, and a REST API for CI. The same test data is available everywhere a tester or pipeline needs it.",
      },
      {
        title: "Free to start, no credit card",
        description:
          "The free tier covers solo QA engineers and small teams. Paid tiers add seats, sharing, and higher API quotas.",
      },
    ],
    howItWorks: [
      {
        title: "Create a list and generate dummy data",
        body: "Spin up a list (e.g. 'Staging users') and bulk-paste or generate fake names, emails, UUIDs, and IDs in seconds.",
      },
      {
        title: "Curate the assets worth reusing",
        body: "Keep the staging admin login, the test card numbers, the sample webhook payload — discard the rest.",
      },
      {
        title: "Paste into any form, app, or pipeline",
        body: "Hit the shortcut in the browser, desktop, or mobile app — or pull values via the REST API in your CI test data setup.",
      },
    ],
    useCases: [
      "QA engineers needing a hosted test data generator that doesn't lose values between sessions",
      "Teams replacing scattered Faker.js scripts with a shared catalog",
      "Mockaroo users who want to keep generated data instead of regenerating it every run",
      "Playwright and CI pipelines pulling sample test data via API",
      "Demo and sales engineers pasting realistic dummy data into product walkthroughs",
    ],
    faqs: [
      {
        q: "Is TestAssets a free test data generator?",
        a: "Yes. The free tier lets you create lists, generate fake data, and paste it from the web app, browser extension, and desktop app. Paid tiers unlock more seats, sharing, and higher API quotas.",
      },
      {
        q: "How is this different from Mockaroo or Faker.js?",
        a: "Mockaroo and Faker.js are great at producing random rows on demand. TestAssets adds the missing layer above them: a persistent, shared catalog of the test data your team actually reuses, with paste-everywhere access from browser, desktop, mobile, and a REST API.",
      },
      {
        q: "What kinds of test data can I generate?",
        a: "Anything that fits in a string field — names, emails, addresses, phone numbers, UUIDs, sample tokens, JSON payloads, staging credentials, test card numbers. Bulk-paste lets you import hundreds of values at once.",
      },
      {
        q: "Can I use generated test data in my CI pipeline?",
        a: "Yes. Every asset is exposed through the REST API and an MCP server, so Playwright suites, k6 load tests, GitHub Actions, and AI agents can read the same test data your QA team uses by hand.",
      },
      {
        q: "Will my generated test data leak real personal information?",
        a: "No. TestAssets is built for non-secret, non-production data. Generated dummy data is synthetic by design, and the API never accepts production exports.",
      },
    ],
  },
  {
    slug: "playwright-test-data",
    icon: Code,
    metaTitle: "Test Data for Playwright Suites | TestAssets",
    metaDescription:
      "Stop hardcoding fixtures in your Playwright tests. TestAssets is a hosted catalog of credentials, IDs, and payloads your Playwright suite reads via REST API.",
    eyebrow: "Playwright",
    h1: "Test Data for Playwright That Doesn't Rot",
    intro:
      "Playwright suites get fast and stable when test data lives outside the repo. TestAssets is a hosted catalog of credentials, fixtures, IDs, and payloads — your Playwright suite reads them via REST API, your QA team pastes them from a browser extension. One source of truth.",
    primaryCta: { label: "Get a free API key", to: "/auth" },
    secondaryCta: { label: "Read the API docs", to: "/api-docs" },
    benefits: [
      {
        title: "No more hardcoded credentials",
        description:
          "Staging logins, admin tokens, and test cards live in a hosted catalog, not in your fixture files. Rotate once, every test sees the new value.",
      },
      {
        title: "Drop-in custom fixture",
        description:
          "A 10-line custom Playwright fixture pulls your test data at runtime. No new dependency, no SDK, no DSL — just `fetch`.",
      },
      {
        title: "Same data your QA team uses",
        description:
          "Manual testers paste the same credentials from a browser extension or desktop app. Automation and humans never drift apart.",
      },
      {
        title: "globalSetup and storageState ready",
        description:
          "Pre-authenticate once in globalSetup using credentials pulled from TestAssets, save the session, run hundreds of tests instantly.",
      },
    ],
    howItWorks: [
      {
        title: "Create a list of test assets",
        body: "Add staging credentials, test card numbers, sample payloads, and any other reusable test data to a TestAssets list.",
      },
      {
        title: "Generate a free API key",
        body: "From the Settings page, generate a read-only API key for your CI pipeline. Add it to GitHub Actions secrets.",
      },
      {
        title: "Read from a Playwright fixture",
        body: "Write a one-time custom fixture that fetches your list via REST API and exposes it to every test. Done.",
      },
    ],
    useCases: [
      "Centralizing staging credentials shared across QA, CI, and manual testers",
      "Rotating test data without touching fixture files in the repo",
      "Pre-authenticating Playwright via globalSetup using shared logins",
      "Sharing API payloads between Playwright API tests and Postman/Insomnia",
      "Keeping test data accessible to AI agents via the MCP server alongside the same Playwright suite",
    ],
    faqs: [
      {
        q: "How do I use TestAssets in a Playwright fixture?",
        a: "Extend Playwright's base test, add a custom fixture that calls `fetch('https://api.testassets.io/assets?list=<your-list>', { headers: { apikey: process.env.TESTASSETS_KEY } })`, and expose the result to your tests. Full example in the API docs.",
      },
      {
        q: "Does this work with globalSetup and storageState?",
        a: "Yes. Fetch your staging credentials in the globalSetup script, drive the login flow once, and save the storageState file Playwright loads on every test.",
      },
      {
        q: "Is there a Playwright SDK?",
        a: "No SDK — it is a plain REST API. That keeps your test runtime dependencies tiny and avoids version-coupling between TestAssets and Playwright releases.",
      },
      {
        q: "Can I use the same data in Cypress, Selenium, or my CI scripts?",
        a: "Yes. The REST API is framework-agnostic. The same list works in Playwright, Cypress, Selenium, k6, GitHub Actions, or any HTTP client.",
      },
      {
        q: "Is it free for CI use?",
        a: "Yes. The free tier covers solo developers and small teams. Paid tiers raise API quotas and add seats for larger teams.",
      },
    ],
  },
];

export const getFeaturePage = (slug: string) =>
  featurePages.find((p) => p.slug === slug);